import {Capture, CaptureEventIds, SktErrors, CaptureDeviceType, CaptureEventTypes, 
  CaptureProperty, CapturePropertyIds, CapturePropertyTypes} from 'socketmobile-capturejs';
import CaptureDataSourceID from '../helpers/captureDataSourceIds';
import PropertyDefinition from '../helpers/propertyIDs.json';

const rootCaptureGuid = '00000000-0000-0000-0000-000000000000';
const rootDeviceType = 0;
const rootName = 'Capture root';
const hexadecimalBase = 16;
let captureInstance = null;
let openingCapture = false;
let captureOpen = false;
let rootDevices = []; // normal only one device
let devices = [];
let deviceManagers = [];
let notificationCallback;

const logger = {
  log: (message, arg) => {
    if(arg){
      console.log(message, arg);
    }
    else {
      console.log(message);
    }
  }
};

const getSetTypeFromPropId = propId => {
  const index = PropertyDefinition.properties.findIndex(p => p.int32 === ''+propId);
  if(index === -1){
    throw(`no property found for ID: ${propId}`);
  }
  const name = PropertyDefinition.properties[index].SetType;
  const value = PropertyDefinition.Types[name].value;
  return { name, value };
}

const getGetTypeFromPropId = propId => {
  const index = PropertyDefinition.properties.findIndex(p => p.int32 === ''+propId);
  if(index === -1){
    throw(`no property found for ID: ${propId}`);
  }
  const name = PropertyDefinition.properties[index].GetType;
  const value = PropertyDefinition.Types[name].value;
  return { name, value };
}

const getRootCaptureFromPropId = propId => {
  const index = PropertyDefinition.properties.findIndex(p => p.int32 === ''+propId);
  if(index === -1){
    throw(`no property found for ID: ${propId}`);
  }
  const root = PropertyDefinition.properties[index].Capture;
  return root;
}


const addDevice = list => device => {
  if(list.findIndex(d => d.guid === device.guid) == -1){
    list.push(device);
  }
}; 

const removeDevice = list => device => {
  const index = list.findIndex(d => d.guid === device.guid);
  if(index !== -1){
    list.splice(index, 1);
  }
};

const addDeviceToRootDevices = addDevice(rootDevices);
const addDeviceToDevices = addDevice(devices);
const addDeviceToManagers = addDevice(deviceManagers);
const removeDeviceFromRootDevices = removeDevice(rootDevices);
const removeDeviceFromDevices = removeDevice(devices);
const removeDeviceFromManagers = removeDevice(deviceManagers);

const getDeviceFromList = keyObj => {
  let key = Object.keys(keyObj)[0];
  if(!keyObj[key]){
    throw(new Error(`the keyObj ${JSON.stringify(keyObj)} should have a key handle or guid`));
  }
  const value = keyObj[key];
  let list = devices;

  let index = list.findIndex(d => d[key] === value);
  if(index  === -1){
    list = deviceManagers;
    index = list.findIndex(d => d[key] === value);
  }
  if(index !== -1){
    return list[index];
  }
  return undefined;
};

const addDecodedData = (handle, decodedData) => {
  const device = devices.find(d => d.handle === handle);
  if(device){
    device.notifications = device.notifications || [];
    device.notifications.push(`DecodedData DataSourceId: ${decodedData.id} Name: ${decodedData.name} Data:${decodedData.data}`); 
  }
};

const addErrorEvent = (handle, error) => {
  const device = [...devices, ...deviceManagers, ...rootDevices].find(d => d.handle === handle);
  if(device){
    device.notifications = device.notifications || [];
    device.notifications.push(`Receive Error: ${error}`); 
  }
};

const addPowerState = (handle, powerState) => {
  const device = devices.find(d => d.handle === handle);
  if(device){
    device.notifications = device.notifications || [];
    device.notifications.push(`Power state: ${powerState}`); 
  }
};

const addButtonState = (handle, buttonState) => {
  const device = devices.find(d => d.handle === handle);
  if(device){
    device.notifications = device.notifications || [];
    device.notifications.push(`Button state: ${buttonState}`); 
  }
};

const addBatteryLevel = (handle, batteryLevel) => {
  const level = (batteryLevel >> 8) & 0xFF;
  const device = devices.find(d => d.handle === handle);
  if(device){
    device.notifications = device.notifications || [];
    device.notifications.push(`Battery Level: ${level}%`); 
  }
};

const addDiscoveredDevice = (handle, deviceDiscovered) => {
  const deviceManager = deviceManagers.find(dm => dm.handle === handle);
  if(deviceManager){
    deviceManager.notifications = deviceManager.notifications || [];
    deviceManager.notifications.push(`Device Discovered: ${deviceDiscovered}`); 
  }
};

const addDiscoveryEnd = (handle) => {
  const deviceManager = deviceManagers.find(dm => dm.handle === handle);
  if(deviceManager){
    deviceManager.notifications = deviceManager.notifications || [];
    deviceManager.notifications.push('Device Discovery Ended'); 
  }
};

const addDeviceOwnership = (handle, ownership) => {
  const device = devices.find(d => d.handle === handle);
  if(device){
    device.notifications = device.notifications || [];
    device.notifications.push(`Ownership: ${ownership}`); 
  }
}

const captureHandler = async(event, handle) => {
  console.log('notification', {event, handle} );
  let device;
  let devicesListChanged = false;
  if(!event || !event.id) return;
  switch(event.id){
    case CaptureEventIds.DeviceArrival:
      addDeviceToDevices(event.value);
      devicesListChanged = true;
      break;
    case CaptureEventIds.DeviceRemoval:
      // close the device first and then remove it
      device = getDeviceFromList({ guid: event.value.guid });
      if(device){
        if(device.handle){
          await closeDevice(device.handle);
        }
      }
      removeDeviceFromDevices(event.value);
      devicesListChanged = true;
      break;
    case CaptureEventIds.DeviceManagerArrival:
      addDeviceToManagers(event.value);
      devicesListChanged = true;
      break;
    case CaptureEventIds.DeviceManagerRemoval:
      // close the device first and then remove it
      device = getDeviceFromList({ guid: event.value.guid });
      if(device){
        if(device.handle){
          await closeDevice(device.handle);
        }
      }
      removeDeviceFromManagers(event.value);
      devicesListChanged = true;
      break;
    case CaptureEventIds.DeviceOwnership:
      addDeviceOwnership(handle, event.value);
      break;
    case CaptureEventIds.DecodedData:
      addDecodedData(handle, event.value);
      break;
    case CaptureEventIds.Terminate:
      break;
    case CaptureEventIds.Error:
      addErrorEvent(handle, event.value);
      break;
    case CaptureEventIds.Power:
      addPowerState(handle, event.value);
      break;
    case CaptureEventIds.Buttons:
      addButtonState(handle, event.value);
      break;
    case CaptureEventIds.BatteryLevel:
      addBatteryLevel(handle, event.value);
      break;
    case CaptureEventIds.ListenerStarted:
      break;
    case CaptureEventIds.DeviceDiscovered:
      addDiscoveredDevice(handle, event.value);
      break;
    case CaptureEventIds.DiscoveryEnd:
      addDiscoveryEnd(handle);
      break;
  }
  if(notificationCallback){
    try{
      notificationCallback(handle, devicesListChanged);
    }
    catch(err) {
      console.error(err);
    }
  }
}

const openClient = async (url, appInfo) => {
  const options = {
    host: url
  };

  if((openingCapture === true)||(captureOpen === true)){
    return;
  }
  openingCapture = true;
  if(captureInstance === null) {
    captureInstance = new Capture(logger);
  }
  try {
    await captureInstance.open(appInfo, captureHandler, options);
    openingCapture = false;
    captureOpen = true;
    const handle = captureInstance.clientOrDeviceHandle;
    const client = {
      name: rootName,
      guid: rootCaptureGuid,
      type: rootDeviceType,
      captureDevice: captureInstance,
      handle
    };
    addDeviceToRootDevices(client);
    return client;
  } 
  catch (err) {
    openingCapture = false;
    console.error(err);
    let error = {};
    if(err == SktErrors.ESKT_UNABLEOPENDEVICE){
      error.message = 
        '<p>Error unable to open the client</p>'
      + '<p>Is the Companion Service running?</p>' 
      + '<p>Also, don\'t forget to forward the port:</p>'
      + '<p>adb forward tcp:18481 tcp:18481</p>';
    }
    else{
      error.message = `Error ${err} while opening the client`;
    }
    throw({code: err, message: error.message});
  }
};

const closeClient = async () => {
  if(captureOpen === true){
    await captureInstance.close();
    captureOpen = false;
    removeDeviceFromRootDevices({guid: rootCaptureGuid});
  }
};

const openDevice = async (handle, deviceGuid) => {
  if((captureOpen === true) && 
  (handle === captureInstance.clientOrDeviceHandle)){
    try{
      const captureDevice = new Capture(logger);
      await captureDevice.openDevice(deviceGuid, captureInstance);
      const device = getDeviceFromList({guid: deviceGuid});
      if(device){
        device.captureDevice = captureDevice;
        device.handle = captureDevice.clientOrDeviceHandle;
      }
      return captureDevice.clientOrDeviceHandle;
    }
    catch(err){
      console.error(err);
      let error = {};
      error.message = `Error ${err} while opening the device`;
      error.code = err;
      throw(error);
    }
  }
};

const closeDevice = async handle => {
  if(captureOpen === true){
    const device = getDeviceFromList({ handle });
    if(device){
      try{
        await device.captureDevice.close();
        device.notifications = [];
        device.captureDevice = undefined;
        device.handle = undefined;
      }
      catch(err){
        console.error(err);
      }
    }
  }
};

const getDeviceTypes = () => {
  const keys = Object.keys(CaptureDeviceType);
  const types = keys.map(k => {
    return {name: k, type: CaptureDeviceType[k]};
  });
  return types;
};

const getDataSourceIds = () => {
  return CaptureDataSourceID;
};

const getEventType = () => {
  return CaptureEventTypes;
};

const getEventIds = () => {
  return CaptureEventIds;
};

const getRootCaptures = () => {
  return [...rootDevices];
};

const getDevices = () => {
  return [...devices];
};

const getDeviceManagers = () => {
  return [...deviceManagers];
};

const getAllDevices = (includingRoot=false) => {
  if(includingRoot) {
    return [...devices, ...deviceManagers, ...rootDevices];
  }
  return [...devices, ...deviceManagers];
}

const getDeviceNotifications = handle => {
  const device = getDeviceFromList({ handle });
  if(device){
    return device.notifications || [];
  }
  return [];
}

const resetDeviceNotifications = handle => {
  const device = getDeviceFromList({ handle });
  if(device){
    device.notifications = [];
  }
}

const setNotification = callback => {
  notificationCallback = callback;
}

const getProperties = () => {
  const keys = Object.keys(CapturePropertyIds).filter(isNaN);
  const properties = keys.map(k => {
    const getType = getGetTypeFromPropId(CapturePropertyIds[k]);
    const setType = getSetTypeFromPropId(CapturePropertyIds[k]);
    const rootCapture = getRootCaptureFromPropId(CapturePropertyIds[k]);
    return {
      name: k, 
      id: CapturePropertyIds[k],
      rootCapture,
      get: {
        value: getType.value,
        name: getType.name
      },
      set: {
        value: setType.value,
        name: setType.name
      }
    };
  });
  return properties;
};

const sendProperty = async (handle, get, property) => {
  const device = [...devices, ...deviceManagers, ...rootDevices].find(d => d.handle === handle);
  if(!device){
    throw({
      code: SktErrors.ESKT_INVALIDHANDLE, 
      message: `no devices matching to the handle: ${handle}`
    });
  }
  let value;
  let d = 10;
  switch(property.type.value){
    case CapturePropertyTypes.Byte:
      d = property.byteValue.indexOf('0x')=== -1? 10 : 16; 
      value = parseInt(property.byteValue, d);
      break;
    case CapturePropertyTypes.Ulong:
      d = property.ulongValue.indexOf('0x')=== -1? 10 : 16; 
      value = parseInt(property.ulongValue, d);
      break;
    case CapturePropertyTypes.String:
      value = property.stringValue;
      break;
    case CapturePropertyTypes.Array:
      value = property.arrayValue.split(',').map(v => parseInt(v, hexadecimalBase));
      break;
  }
  let propertyResponse;
  const captureProperty = new CaptureProperty(
    property.id, property.type.value, value 
  );
  if(get){
    propertyResponse = await device.captureDevice.getProperty(captureProperty);
  }
  else {
    propertyResponse = await device.captureDevice.setProperty(captureProperty);
  }
  device.propertyResponses = device.propertyResponses || [];
  device.propertyResponses.push(propertyResponse);
  if(notificationCallback){
    notificationCallback(handle);
  }
  return propertyResponse;
}
/**
 * return the list of response for each properties sent to
 * the device identified by the its handle
 *
 * @param {number} handle
 * @returns an array of string for each property response received
 */
const getDevicePropertyResponses = handle => {
  const device = [...devices, ...deviceManagers, ...rootDevices].find(d => d.handle === handle);
  if(!device){
    throw({
      code: SktErrors.ESKT_INVALIDHANDLE, 
      message: `no devices matching to the handle: ${handle}`
    });
  }
  device.propertyResponses = device.propertyResponses || [];
  return [...device.propertyResponses];
};

export default {
  openClient,
  closeClient,
  openDevice,
  closeDevice,
  getDeviceTypes,
  getDataSourceIds,
  getEventType,
  getEventIds,
  getRootCaptures,
  getDevices,
  getDeviceManagers,
  getAllDevices,
  getDeviceNotifications,
  resetDeviceNotifications,
  setNotification,
  getProperties,
  sendProperty,
  getDevicePropertyResponses
}